import React, { useState } from 'react';

import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { orderBy } from 'lodash';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { Error, Loading } from 'components/Fetch';
import { getAdminsByGid, selectCurrentGid } from 'store/groups/reducer';
import { formatDropdownOptions } from 'utils/Object';
import { Dropdown } from 'components/Forms';

interface IBulkAssignTasksPayload {
  ids: number[];
  assignees: string[];
}

interface IBulkAssignTasksModalProps {
  handleClose: (refresh?: boolean) => void;
}

const UNASSIGNED_VALUE = 'unassignedKey';

const BulkAssignTasksModal = ({ handleClose }: IBulkAssignTasksModalProps) => {
  const intl = useIntl();
  const selectedGid = useAppSelector(selectCurrentGid);
  const admins = useAppSelector((state) => getAdminsByGid(state, selectedGid));
  const assignedOptions = orderBy(
    formatDropdownOptions(admins, 'aid', 'name'),
    [(option) => option.label.toLowerCase(), 'name'],
    'asc'
  );
  assignedOptions.unshift({
    label: intl.formatMessage({ id: 'unassignedKey' }),
    value: UNASSIGNED_VALUE
  });
  const [assignTo, setAssignTo] = useState<string[]>([]);
  const selectedTasks = useAppSelector((state) => state.clinicalLogger.tasks);

  const [{ error: errorPatchTask, loading: loadingPatchTasks }, patch] =
    useAxios<IBulkAssignTasksPayload>(
      {
        url: '/clinician-gateway/Tasks',
        method: 'PATCH'
      },
      { manual: true }
    );

  const handleSubmit = async () => {
    const ids = selectedTasks.map((task) => task.id);
    const noAssignees = assignTo.length === 1 && assignTo[0] === UNASSIGNED_VALUE;
    const payload: IBulkAssignTasksPayload = { ids, assignees: noAssignees ? [] : assignTo };
    await patch({ data: payload });
    handleClose(true);
  };

  const handleChange = (values: string[], selected?: string) => {
    let assignees = values;
    if (selected === UNASSIGNED_VALUE) {
      assignees = [UNASSIGNED_VALUE];
    } else if (
      assignees.length > 1 &&
      assignees.some((assignee) => assignee === UNASSIGNED_VALUE)
    ) {
      assignees = assignees.filter((assignee) => assignee !== UNASSIGNED_VALUE);
    }
    setAssignTo(assignees);
  };
  return (
    <Modal show onHide={handleClose} backdrop="static" data-test="bulk-update-modal">
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({ id: 'assignKey' })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorPatchTask && <Error retry={() => handleSubmit()} />}
        {loadingPatchTasks && <Loading className="mt-4" />}
        <Row>
          <Form.Group className="form-group" as={Col}>
            <Dropdown
              name="assignees"
              label={intl.formatMessage({ id: 'assignedTo' })}
              dataTestId="bulk-clinical-tasks-assignees"
              placeholder={intl.formatMessage({ id: 'selectUserKey' })}
              options={assignedOptions}
              onChange={(values: ISelectOption[] | null, action?: any) => {
                handleChange(
                  values ? values.map((value) => value.value) : [],
                  action?.option?.value
                );
              }}
              value={assignedOptions.filter((opt) => assignTo.includes(opt.value))}
              isClearable={false}
              isMulti
              isSearchable
              useAvatarNameColor
              variant="form"
            />
          </Form.Group>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="link"
          onClick={() => handleClose()}
          data-test="careplan-clinical-tasks-modal-close-btn"
        >
          {intl.formatMessage({ id: 'closeKey' })}
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={handleSubmit}
          data-test="careplan-clinial-tasks-modal-submit-btn"
        >
          {intl.formatMessage({ id: 'submitKey' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BulkAssignTasksModal;
